/**
 * Raw form inputs without labels
 */
import { NumberInput } from '@linked/controls';
import { makeStyles, Popover, styled, IconButton } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import { Linked, useLinked } from '@type-r/react';
import * as React from 'react';
import { useContext, KeyboardEvent, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import { colors, div } from '../common';
import { asCurrency } from '../formatters';
import CloseIcon from '@material-ui/icons/Close';

export const ReadOnly = React.createContext( false );

export interface LxValueProps<T> {
    $value? : Linked<T>;
    value? : T;
}

export interface LxInputProps<T> extends LxValueProps<T> {
    readOnly? : boolean;

    /** Used to display permanent star on some input types, use validators instead */
    required? : boolean;
}

export type InputProps = Omit<BaseInputProps, 'classes'>

export interface BaseInputProps {
    /** Class names for the input, error, and number. Override style in unique situations as a last resort. */
    classes? : {
        input? : string,
        error? : string,
        number? : string
    }

    /** Override style in unique situations as a last resort */
    className? : string,

    readOnly? : boolean,

    /** Used to display permanent star on some input types, use validators instead */
    required? : boolean,

    disabled? : boolean,

    placeholder? : string,

    currency? : boolean,

    /** date input type */
    date? : boolean,

    /** number input type */
    number? : boolean,

    /** integer input type */
    integer? : boolean,

    /** positive number or positive integer */
    positive? : boolean,

    startDate? : Date
    endDate? : Date
    selectsEnd? : boolean,
    selectsStart? : boolean,

    id? : string,

    'qa-id'? : string,

    onKeyPress? : ( event: KeyboardEvent<HTMLInputElement> ) => void,
    onPaste? : React.ClipboardEventHandler<HTMLInputElement>,

    /** For the date input, set minimal time */
    minDate? : Date,

    /** For the date input, set maximum time */
    maxDate? : Date,

    /** Linked value. Required for non read-only fields */
    $value? : Linked<string> | Linked<number> | Linked<Date>

    /** Provinding the value will make input read-only */
    value? : string | number | Date,

    onBlur?: (event) => void,

    type? : string
}

const DateInputElement = forwardRef(( props, ref : any ) => (
    <input {...props} ref={ref}/>
));

/**
 * Text, numeric, and date inputs are based on `BaseInput` component and share the same props and behavior.
 *
 * - `BaseInput` is not styled and not intended to be used directly.
 * - `Input` - styled version of BaseInput, use as an inline input without the label (in grids).
 * - `FormInput` - Input with horizontally alighed label displaying inline error text. Main building block of forms.
 * - `CompactInput` - Input with embedded label to be used in forms with vertical layout.
 */
export const BaseInput =
    ({ $value, value, classes, placeholder, readOnly, number, currency, className , date, positive, integer, ...props } : BaseInputProps ) => {
        const ro = useContext( ReadOnly ) || readOnly || !$value;

        let cs =  className ? classes.input + ' ' + className : classes.input ;

        if( !ro && $value.error && !props.disabled && !($value.error === 'Required' && props.required === false) ) cs += ' ' + classes.error;

        if( date ){
            return ro ?
                <DatePicker
                    dateFormat="MM/dd/yyyy"
                    placeholderText={placeholder || 'MM/DD/YYYY'}
                    onChange={ () => undefined }
                    readOnly={ true }
                    customInput={ <DateInputElement data-qa-id={ props['data-qa-id'] } /> }
                    className={ cs }
                    selected={ ( $value ? $value.value : value ) as Date }
                    {...props}
                />
            :
                <DatePicker
                    dateFormat="MM/dd/yyyy"
                    placeholderText={placeholder || 'MM/DD/YYYY'}
                    className={ cs + ' date-picker' }
                    popperContainer={ StopClicks }
                    popperPlacement={ 'auto '}
                    customInput={ <DateInputElement data-qa-id={ props['data-qa-id'] } /> }
                    selected={ $value.value as Date }
                    onChange={ date => ( $value as Linked<Date>).set( date ) }
                    {...props}
                />
        }

        const isNumber = number || positive || integer || currency;
        if( isNumber ) cs += ' ' + classes.number;

        return !ro && isNumber ?
            <NumberInput {...props as any} positive={ positive } placeholder={ placeholder } integer={ integer } className={ cs } $value={$value as Linked<number> } />
            :
            <input
                readOnly={ ro }
                className={ cs }
                placeholder = { placeholder }
                value={ valueToText( $value as any, value, currency && ro ? asCurrency : dummy ) }
                onChange={ e => ( $value as Linked<string>).set( e.target.value ) }
                { ...props }
            />
    }


const StopClicks = ({ children }) =>
    <div onClick={ e => {
        e.preventDefault();
    }}>
        { children }
    </div>

export const inputStyles = {
    '&.date-picker' : {
        background: 'url(assets/images/Icon_Calendar.png) no-repeat scroll',
        paddingRight: 5,
        backgroundOrigin: 'content-box',
        backgroundPosition: 'center right',
    }
}

export const Input = withStyles({
    input : {
        ...inputStyles,
        width: '100%',
        borderWidth: 1,
        borderStyle: 'solid',
        boxSizing: 'border-box',
        borderRadius: 2,
        backgroundColor: '#FFF',
        border: '1px solid #E3E9ED',
        color: '#000',
        fontFamily: 'Montserrat',
        fontSize: 14,
        fontWeight: 500,
        padding: '4px 3px 3px 3px',

        '&:focus': {
            backgroundColor: '#F6FCFF',
            border: '1px solid #1084D3',
            outline: 'none',
        },

        '&:disabled' :{
            backgroundColor: 'rgba(227,233,237,0.25)',
            border: '1px solid #E3E9ED',
        },

        '&:read-only' : {
            border: 'none'
        },

        '&:hover:read-write' : {
            border: '1px solid #1084D3',
        }
    },

    number : {
        textAlign : 'end'
    },

    error : {
        border: '1px solid #FF274B !important',
    }
})( BaseInput )

function dummy( x ){
    return x;
}

function valueToText( $value : Linked<string|number>, value : any, formatter = dummy ){
    const x = $value ? $value.value : value;
    return x == null ? '' : formatter( x );
}

function maybeError( className : string, $value : Linked<any> ){
    return $value && $value.error ? className + ' error' : className;
}


export const PopoverValue = ({ label, children, withCloseIcon = true, anchorAlign = null, transformAlign = null}) => {
    const $open = useLinked( null ),
        { label: labelClass, iconButton : iconButtonClass, ...classes } = usePopoverValueStyles();

    return (
    <>
        <span
            className={ labelClass }
            onClick={ event => $open.set( event.currentTarget ) }
        >
            { label }
        </span>
        <Popover
            classes={ classes }
            open={ Boolean( $open.value ) }
            anchorEl={ $open.value }
            onClose={ $open.null }
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: anchorAlign || 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: transformAlign || 'center',
            }}
        >
            { children }
            { withCloseIcon ? 
            <IconButton aria-label="Close" size="small" className={ iconButtonClass } onClick={ $open.null }>
                <CloseIcon/>
            </IconButton> : undefined }
        </Popover>
    </>
    )
}

const usePopoverValueStyles = makeStyles({
    label : {
        "&:hover" : {
            textDecoration: 'underline',
            cursor : 'pointer'
        },

        fontStyle: 'italic'
    },

    paper : {
        padding: 8
    },

    iconButton: {
        position: 'absolute',
        top: 7,
        right: 7
    }
})

export const FormValue = div({
    fontFamily: 'Montserrat',
    flex: 3,
    color: 'black',
    alignSelf: 'center',
    fontSize: 14,
    fontWeight: 500,
});

export const PrevFormValue = div({
    fontFamily: 'Montserrat',
    flex: 3,
    alignSelf: 'center',
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'line-through',
    color : colors.red
});

function dummyFormatter( x ){
    return x;
}

export const Changes = styled(
    ({ children, prev, className, formatter = dummyFormatter }) =>{
        const changed = children !== prev;
    
        return changed ?
            <div className={className}>
                { prev ? <div className="prev-value">{formatter(prev)}</div> : null }
                <div className="curr-value">{formatter(children)}</div>
            </div>
        :
            formatter( children )
    })({
        '& .curr-value' : {
            color : colors.red,
            backgroundColor : '#FCF8F8'
        },
    
        '& .prev-value' : {
            textDecoration: 'line-through',
            color : colors.grey,
            width: "100%"
        }
    }
)

const defaultRenderer = x => <FormValue key={ x }>{ x }</FormValue>;

export const PopoverList = ({ items, children = defaultRenderer } : {
    items : any[],
    children? : ( x : any ) => JSX.Element
}) =>
    items.length > 1 ?
        <PopoverValue label={
            `Click to show (${items.length} items)`
        }>
            { items.map( x => children( x ) ) }
        </PopoverValue>
    :
    items.length ? children( items[ 0 ] ) :
    <div/>;